import React, { useState, useEffect } from "react";
import { Wifi, WifiOff, Activity } from "lucide-react";
import { onLatency, getLatency } from "../../Utils";

export function NetworkStats() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [speed, setSpeed] = useState<number | null>(null);
  const [ping, setPing] = useState<number | null>(getLatency);
  const [connectionType, setConnectionType] = useState<string | null>(null);

  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    const conn =
      (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection;

    const updateConnectionInfo = () => {
      if (conn) {
        setSpeed(conn.downlink ?? null);
        setConnectionType(conn.effectiveType ?? null);
      }
    };

    if (conn) {
      updateConnectionInfo();
      conn.addEventListener("change", updateConnectionInfo);
    }

    const unsubscribe = onLatency((avgMs) => {
      setPing(avgMs);
    });

    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
      if (conn) {
        conn.removeEventListener("change", updateConnectionInfo);
      }
      unsubscribe();
    };
  }, []);

  if (!isOnline) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-500 text-xs font-semibold animate-pulse">
        <WifiOff className="w-3.5 h-3.5" />
        <span>Offline</span>
      </div>
    );
  }

  const getNetworkStatus = () => {
    const p = ping;
    const s = speed;

    if (p !== null) {
      if (p >= 300) return { label: "Low", color: "text-rose-500" };
      if (p >= 120) return { label: "Medium", color: "text-amber-500" };
      return { label: "High", color: "text-emerald-500" };
    }

    if (s !== null) {
      if (s < 2) return { label: "Low", color: "text-rose-500" };
      if (s < 10) return { label: "Medium", color: "text-amber-500" };
      return { label: "High", color: "text-emerald-500" };
    }

    return { label: "High", color: "text-emerald-500" };
  };

  const status = getNetworkStatus();

  return (
    <div className="flex flex-col gap-1.5 px-3 py-2 rounded-lg bg-gray-100/60 dark:bg-gray-800/40 border border-gray-200/50 dark:border-gray-700/30 text-xs transition-all duration-200">
      <div className="flex items-center justify-between text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1.5 font-medium">
          <Wifi className={`w-3.5 h-3.5 ${status.color}`} />
          Internet
        </span>
        <span
          className={`text-[9px] uppercase font-bold tracking-wider ${status.color}`}
        >
          {status.label}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="font-bold text-gray-700 dark:text-gray-200">
          {speed ? `${speed.toFixed(1)} Mbps` : (connectionType ?? "Online")}
        </span>
        {ping !== null && (
          <span className="flex items-center gap-1 text-[10px] text-gray-400">
            <Activity className="w-3 h-3 text-gray-400/70" />
            {ping}ms
          </span>
        )}
      </div>
    </div>
  );
}
